import React, { useState, useEffect } from 'react'
import { Card, Table, Select, Button, message, Spin, Tag, Tooltip, Space, Alert } from 'antd'
import { SaveOutlined, ReloadOutlined, InfoCircleOutlined, LockOutlined } from '@ant-design/icons'
import axios from 'axios'
import { connect } from 'react-redux'

const { Option } = Select

const UserPermissions = ({ userId, userType, onPermissionsChange, onGetPendingChanges, currentUser, userPermissions, teamSlug }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [permissionsData, setPermissionsData] = useState(null)
  const [changes, setChanges] = useState({})
  const [hasChanges, setHasChanges] = useState(false)
  const [hasPermission, setHasPermission] = useState(true)

  useEffect(() => {
    // Check if user has permission to manage user permissions
    const canManagePermissions = userPermissions.includes('allow_user_admin');
    setHasPermission(canManagePermissions);

    if (userId && canManagePermissions) {
      loadUserPermissions()
    }
  }, [userId, userPermissions])

  // Expose pending changes and save function to parent component
  useEffect(() => {
    if (onGetPendingChanges) {
      onGetPendingChanges(changes, hasChanges, savePermissions)
    }
  }, [changes, hasChanges, onGetPendingChanges])

  const loadUserPermissions = async () => {
    if (!userId) return

    setLoading(true)
    try {
      const response = await axios.get(`/api/teams/${teamSlug}/users/${userId}/permissions`)
      setPermissionsData(response.data)
      setChanges({})
      setHasChanges(false)
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setHasPermission(false)
        message.error('You do not have permission to manage user permissions')
      } else {
        message.error('Failed to load user permissions')
      }
      console.error('Error loading permissions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePermissionChange = (token, status) => {
    const newChanges = {
      ...changes,
      [token]: { status }
    }
    setChanges(newChanges)
    setHasChanges(Object.keys(newChanges).length > 0)
  }

  const savePermissions = async (showMessages = false) => {
    if (!hasChanges) return { success: true }

    setSaving(true)
    try {
      const permissions = Object.entries(changes).map(([token, change]) => ({
        token,
        status: change.status
      }))

      const response = await axios.put(`/api/teams/${teamSlug}/users/${userId}/permissions`, {
        permissions
      })

      if (showMessages) {
        message.success('User permissions updated successfully')
      }

      setPermissionsData(prev => ({
        ...prev,
        effective_permissions: response.data.effective_permissions
      }))
      setChanges({})
      setHasChanges(false)

      if (onPermissionsChange) {
        onPermissionsChange(response.data.effective_permissions)
      }

      // Reload to get fresh data
      await loadUserPermissions()

      return { success: true }
    } catch (error) {
      if (showMessages) {
        message.error('Failed to update user permissions')
      }
      console.error('Error saving permissions:', error)
      return { success: false, error }
    } finally {
      setSaving(false)
    }
  }

  const resetChanges = () => {
    setChanges({})
    setHasChanges(false)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'granted': return 'green'
      case 'denied': return 'red'
      case 'inherit': return 'blue'
      default: return 'default'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'granted': return 'Granted'
      case 'denied': return 'Denied'
      case 'inherit': return 'Inherit from Group'
      default: return 'Unknown'
    }
  }

  const getCurrentStatus = (permission) => {
    if (changes[permission.token]) {
      return changes[permission.token].status
    }
    return permission.user_permission_status
  }


  const columns = [
    {
      title: 'Permission',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <strong>{text}</strong>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Group Permission',
      key: 'group_permission',
      width: 120,
      render: (_, record) => (
        <Tag color={record.group_has_permission ? 'green' : 'default'}>
          {record.group_has_permission ? 'Granted' : 'Not Granted'}
        </Tag>
      ),
    },
    {
      title: 'User Override',
      key: 'user_override',
      width: 180,
      render: (_, record) => (
        <Select
          value={getCurrentStatus(record)}
          onChange={(value) => handlePermissionChange(record.token, value)}
          style={{ width: '100%' }}
          size="small"
        >
          <Option value="inherit">
            <Tag color="blue">Inherit from Group</Tag>
          </Option>
          <Option value="granted">
            <Tag color="green">Grant</Tag>
          </Option>
          <Option value="denied">
            <Tag color="red">Deny</Tag>
          </Option>
        </Select>
      ),
    },
    {
      title: 'Final Result',
      key: 'final_result',
      width: 120,
      render: (_, record) => {
        const currentStatus = getCurrentStatus(record)
        let finalResult = 'denied'

        if (currentStatus === 'granted') {
          finalResult = 'granted'
        } else if (currentStatus === 'inherit' && record.group_has_permission) {
          finalResult = 'granted'
        } else if (currentStatus === 'denied') {
          finalResult = 'denied'
        }

        return (
          <Tag color={finalResult === 'granted' ? 'green' : 'red'}>
            {finalResult === 'granted' ? 'Granted' : 'Denied'}
          </Tag>
        )
      },
    },
  ]

  if (!hasPermission) {
    return (
      <Card title="User Permissions" style={{ marginTop: 16 }}>
        <Alert
          message="Access Denied"
          description="You do not have permission to manage user permissions. Contact an administrator if you need access."
          type="error"
          icon={<LockOutlined />}
          style={{ margin: 20 }}
        />
      </Card>
    )
  }

  if (!userId) {
    return (
      <Card title="User Permissions" style={{ marginTop: 16 }}>
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          Save the user first to manage permissions
        </div>
      </Card>
    )
  }

  if (loading) {
    return (
      <Card title="User Permissions" style={{ marginTop: 16 }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading permissions...</div>
        </div>
      </Card>
    )
  }

  if (!permissionsData) {
    return (
      <Card title="User Permissions" style={{ marginTop: 16 }}>
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No permissions data available
        </div>
      </Card>
    )
  }

  return (
    <Card

      style={{ marginTop: 16 }}
      extra={
        hasChanges && (
          <Button size="small" onClick={resetChanges}>
            Reset Changes
          </Button>
        )
      }
    >
      {/* <div style={{ marginBottom: 16 }}>
        <div><strong>User:</strong> {permissionsData.user.name} ({permissionsData.user.email})</div>
        <div><strong>Access Group:</strong> {permissionsData.user.access_group || 'None'}</div>
        <div><strong>Current Effective Permissions:</strong> {permissionsData.effective_permissions.length} permissions</div>
      </div> */}

      {permissionsData.permissions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No permissions available for this user's access group.
          <br />
          Contact an administrator to configure group permissions.
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={permissionsData.permissions}
          rowKey="token"
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
        />
      )}

      {hasChanges && (
        <div style={{
          marginTop: 16,
          padding: 12,
          backgroundColor: '#fff7e6',
          border: '1px solid #ffd591',
          borderRadius: 4
        }}>
          <strong>Unsaved Changes:</strong> {Object.keys(changes).length} permission(s) modified
        </div>
      )}
    </Card>
  )
}

const mapStateToProps = (state) => ({
  currentUser: (state.user && state.user.data) || null,
  userPermissions: (state.user && state.user.permissions) || [],
  teamSlug: (state.team && state.team.slug) || window.location.pathname.split('/')[3] || 'default',
})

export default connect(mapStateToProps)(UserPermissions)
